import useDisableScroll from "@/hooks/useDisableScroll";
import useIsPortrait from "@/hooks/useIsPortrait";
import { t } from "i18next";
import { throttle } from "lodash";
import { useEffect, useRef, useState } from "react";
import { Button } from "react-aria-components";
import { Trans } from "react-i18next";
import { GrSync } from "react-icons/gr";
import { IoCloseOutline } from "react-icons/io5";
import Webcam from "react-webcam";
import Modal from "./Modal";

// Enum to define camera facing modes
enum FacingMode {
  USER = "user",
  ENVIRONMENT = "environment",
}

// Define video format options and file extensions
const options = { mimeType: "video/mp4" };
const extension = ".mp4";

// Camera component
export function Camera({
  onClose,
  onChange,
  maxRecordTime = 120,
  translationSuffix = "",
}: {
  onClose: () => void;
  onChange: (file: File) => void;
  maxRecordTime?: number;
  translationSuffix?: string;
}) {
  const MAX_RECORD_TIME = {
    label: new Date(maxRecordTime * 1000).toISOString().substring(14, 19), // record time in "MM:SS" format
    value: maxRecordTime - 1, // record time in seconds (1 second short to still be accepted)
  };
  // Refs to store webcam instance and media recorder instance
  const webcamRef = useRef<Webcam>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  // State to track recording status
  const [capturing, setCapturing] = useState<true | undefined>(undefined);

  // State for switching camera facing mode
  const [facingMode, setFacingMode] = useState(FacingMode.ENVIRONMENT);

  // State to store the recording start time
  const [startedRecording, setStartedRecording] = useState(0);

  // State to store the recording elapsed time
  const elapsedTime = useRef<number>(0);
  const [elapsedTimeLabel, setElapsedTimeLabel] = useState<string>("00:00");

  // Custom hook to check if device is in portrait mode or not
  const isPortrait = useIsPortrait();

  // Custom hook to disable body scrolling
  useDisableScroll();

  // Function to start capturing video
  const handleStartCaptureClick = () => {
    if (!webcamRef.current?.stream) return;

    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      ...options,
      bitsPerSecond: 3000000,
      videoBitsPerSecond: 3000000,
    });

    mediaRecorderRef.current.addEventListener("dataavailable", handleDataAvailable);
    mediaRecorderRef.current.start();
    setStartedRecording(Date.now().valueOf());

    setCapturing(true);
  };

  // Function triggered when recording data is available
  const handleDataAvailable = ({ data }: BlobEvent) => {
    if (data.size > 0) {
      handleDownload(data);
      onClose();
    }
  };

  // Function to stop capturing video
  const handleStopCaptureClick = () => {
    if (!mediaRecorderRef.current) return;

    setStartedRecording(0);
    mediaRecorderRef.current.stop();
    setCapturing(undefined);
  };

  // Function to handle video download
  const handleDownload = async (blob: Blob) => {
    onChange(
      new File([blob], `${Date.now().valueOf()}${extension}`, {
        type: options.mimeType,
      }),
    );
  };

  // Function to format the recording time
  function formatTime(milliseconds: number) {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);

    // Ensure two digits for minutes and seconds, and three digits for milliseconds
    const formattedMinutes = String(minutes).padStart(2, "0");
    const formattedSeconds = String(seconds).padStart(2, "0");

    return `${formattedMinutes}:${formattedSeconds}`;
  }

  // Effect to update the recording timer display
  useEffect(() => {
    if (!startedRecording) {
      // elapsedTime.current = 0;
      return;
    }

    const throttledUpdateLabel = throttle((time: number) => {
      setElapsedTimeLabel(formatTime(time));
    }, 1000); // updates at most once every 1 second (visually)

    let id = requestAnimationFrame(function updateTime() {
      elapsedTime.current = Date.now() - startedRecording;

      throttledUpdateLabel(elapsedTime.current);

      if (Date.now() - startedRecording >= MAX_RECORD_TIME.value * 1000) {
        handleStopCaptureClick();
      } else {
        requestAnimationFrame(updateTime);
      }
    });

    return () => cancelAnimationFrame(id);
  }, [startedRecording]);

  return (
    <div className="fixed left-0 top-0 z-40 h-[100dvh] w-screen bg-black">
      {!isPortrait && (
        <Modal notClosable={true}>
          <span>
            <Trans i18nKey={"upload-video.camera.modal.landscape-alert"} />
          </span>
        </Modal>
      )}

      <Modal
        actionLabel={t("upload-video.camera.modal.action-label")}
        isModalDismissable={true}
      >
        <h2 className="text-lg font-semibold text-black">
          {t("upload-video.camera.modal.title")}
        </h2>
        <ul className="mt-2 text-sm leading-relaxed text-black">
          <li>
            <Trans i18nKey={"upload-video.camera.modal.instructions.portrait"} />
          </li>
          <li>
            <Trans
              i18nKey={`upload-video.camera.modal.instructions.length${translationSuffix}`}
            />
          </li>
          <li>
            <Trans i18nKey={"upload-video.camera.modal.instructions.content"} />
          </li>
        </ul>
      </Modal>

      {/* Timer display */}
      <div
        className={`absolute inset-x-0 top-0 z-40 flex items-center justify-center p-3 ${
          isPortrait ? "bg-secondary/60" : "bg-transparent"
        }`}
      >
        <div
          className={`${
            startedRecording && "bg-red-500 transition-colors"
          } rounded-full px-3 py-1 text-center font-semibold tabular-nums text-white`}
        >
          {elapsedTimeLabel} / {MAX_RECORD_TIME.label}
        </div>
      </div>

      {/* Webcam feed */}
      <Webcam
        audio={true}
        ref={webcamRef}
        muted
        className="fixed inset-0 top-0 h-full w-full object-contain"
        width="100%"
        minScreenshotHeight={window.innerHeight}
        height="100%"
        videoConstraints={{
          facingMode,
          height: { ideal: 1080 },
          width: { ideal: 1920 },
        }}
      />

      {/* Controls */}
      <div
        className={`absolute ${
          isPortrait
            ? "inset-x-0 bottom-0 grid-cols-3"
            : "right-0  h-full w-fit grid-rows-3"
        } grid items-center  justify-around justify-items-center bg-secondary/60 p-3`}
      >
        {/* Close button */}
        <div className={`${!isPortrait && "row-start-3"}`}>
          <Button onPress={onClose} aria-label="close">
            <IoCloseOutline className="size-12" width={50} height={50} />
          </Button>
        </div>
        {/* Record button */}
        <div className={`${!isPortrait && "row-start-2"}`}>
          <Button onPress={capturing ? handleStopCaptureClick : handleStartCaptureClick}>
            <div
              data-capturing={capturing}
              className="flex h-20 w-20 items-center justify-center rounded-full border-4  border-white transition-colors duration-300 data-[capturing]:border-primary"
            >
              <div
                data-capturing={capturing}
                className="h-16 w-16 rounded-full bg-white transition-colors duration-300 data-[capturing]:bg-primary"
              ></div>
            </div>
          </Button>
        </div>

        {/* Switch camera button (only available when not recording) */}
        {!capturing && (
          <Button
            aria-label="switch camera"
            onPress={() =>
              setFacingMode((old) =>
                old === FacingMode.ENVIRONMENT ? FacingMode.USER : FacingMode.ENVIRONMENT,
              )
            }
          >
            <GrSync className="size-12" width={50} height={50} />
          </Button>
        )}
      </div>
    </div>
  );
}
